import { useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import type { Control, UseFormSetValue } from 'react-hook-form';
import type { InteractionNotification, InteractionReport } from '@/apis/business/agent/callLogs/types';
import Button from 'components/Common/Button';
import { JourneyItem } from './components/TranscriptToolRow';
import { RightColumnSkeleton } from '../Skeletons';
import InteractionSelector from './components/InteractionSelector';
import InteractionNotificationsDrawer from './components/NotificationsDrawer';
import { mergeNotificationLogs } from './components/NotificationsDrawer/mergeNotificationLogs';
import MusicPlayer from './components/MusicPlayer';
import CallSummarySection from './components/CallSummarySection';
import TranscriptSection from './components/TranscriptSection';
import ReportFlagIcon from '@/assets/icons/ReportFlagIcon';

type CallLogItem = {
  id?: number;
  channel?: string;
  start_at?: string;
  interaction_type?: 'call' | 'chat';
  direction?: 'inbound' | 'outbound';
  recording_url?: string;
  summary?: string;
  interaction_journey?: JourneyItem[];
  email_logs?: InteractionNotification[];
  sms_logs?: InteractionNotification[];
};

interface FormValues {
  contact: { call: number | string; [key: string]: unknown };
  note: { description: string };
}

export default function RightColumn({
  isLoading,
  isOpen,
  callLog,
  control,
  contactId,
  chatsCount,
  callsCount,
  isAgencyView = false,
  formatLogDate,
  endReason,
  endReasonLabel,
  handleSelectCallLog,
  setValue,
  formatTranscriptTimestamp,
  downloadTranscript,
  showInteractionTypeFilter = false,
  report,
  onReportClick
}: {
  isLoading: boolean;
  isOpen: boolean;
  callLog?: CallLogItem;
  control: Control<FormValues>;
  contactId?: number;
  chatsCount?: number;
  callsCount?: number;
  isAgencyView?: boolean;
  formatLogDate: (date: string) => string;
  endReason?: string;
  endReasonLabel?: string;
  handleSelectCallLog: (id: number | undefined) => void;
  setValue: UseFormSetValue<FormValues>;
  formatTranscriptTimestamp: (timestamp?: number) => string;
  downloadTranscript: () => void;
  showInteractionTypeFilter?: boolean;
  report?: InteractionReport | null;
  onReportClick?: () => void;
}) {
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notifications = mergeNotificationLogs(callLog?.email_logs, callLog?.sms_logs);
  const showReportButton = !isAgencyView || Boolean(report?.description);

  useEffect(() => {
    setNotificationsOpen(false);
  }, [callLog?.id]);

  const handleInteractionSelect = (id: number | undefined) => {
    setNotificationsOpen(false);
    handleSelectCallLog(id);
  };

  return (
    <Grid
      size={{ xs: 12, lg: 5 }}
      sx={{
        height: { xs: 'auto', lg: '100%' },
        minHeight: 0,
        overflowY: { xs: 'visible', lg: 'auto' },
        overflowX: 'hidden',
        pl: { xs: 0, lg: 1 }
      }}
    >
      <Box sx={{ bgcolor: '#F8F9FA', p: 2, borderRadius: 2, minHeight: '100%' }}>
        {isLoading ? (
          <RightColumnSkeleton />
        ) : (
          <>
            <Stack direction='row' justifyContent='space-between' mb={1.5}>
              <Typography fontWeight={500} fontSize={14}>
                Call Insights
              </Typography>
              {showReportButton && (
                <Button
                  variant='outlined'
                  color='error'
                  size='small'
                  startIcon={<ReportFlagIcon size={20} />}
                  onClick={onReportClick}
                  sx={{
                    textTransform: 'none',
                    minHeight: 32,
                    px: 1.5,
                    fontSize: 14,
                    fontWeight: 500,
                    color: '#DA0808',
                    borderColor: '#DA0808',
                    bgcolor: '#fff',
                    boxShadow: 'none',
                    '&:hover': { borderColor: '#DA0808', bgcolor: '#FFE5E5', boxShadow: 'none' }
                  }}
                >
                  {report?.description ? 'Reported' : 'Report'}
                </Button>
              )}
            </Stack>

            <InteractionSelector
              callLog={callLog}
              control={control}
              contactId={contactId}
              isAgencyView={isAgencyView}
              chatsCount={chatsCount}
              callsCount={callsCount}
              formatLogDate={formatLogDate}
              endReason={endReason}
              endReasonLabel={endReasonLabel}
              notifications={notifications}
              onOpenNotifications={() => setNotificationsOpen(true)}
              onSelect={handleInteractionSelect}
              setValue={setValue}
              showInteractionTypeFilter={showInteractionTypeFilter}
            />

            <InteractionNotificationsDrawer
              open={notificationsOpen}
              onClose={() => setNotificationsOpen(false)}
              notifications={notifications}
              formatDate={formatLogDate}
            />

            {callLog?.interaction_type === 'call' && callLog?.recording_url ? (
              <MusicPlayer url={callLog.recording_url} isActive={isOpen} />
            ) : null}

            {callLog?.interaction_type === 'call' && <CallSummarySection summary={callLog?.summary} />}

            <TranscriptSection
              journey={callLog?.interaction_journey}
              formatTranscriptTimestamp={formatTranscriptTimestamp}
              onDownload={downloadTranscript}
            />
          </>
        )}
      </Box>
    </Grid>
  );
}
